/* useNowcastTimeline — polls the optical-flow nowcast endpoint.
 *
 * Returns null while loading or when no rain is expected. Auto-refreshes
 * every 90 seconds (matches backend cache TTL).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 90 * 1000;
// Only surface the component when peak intensity ≥ this — below this is
// effectively dry and showing "0% next 30 min" adds noise.
const SHOW_THRESHOLD = 8;

export default function useNowcastTimeline(location) {
  const [data, setData] = useState(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!location?.latitude || !location?.longitude) {
      setData(null);
      return undefined;
    }
    let timer;

    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/nowcast/optical-flow`, {
          params: {
            lat: location.latitude,
            lon: location.longitude,
            minutes: 30,
          },
          timeout: 12000,
        });
        if (cancelledRef.current) return;
        const d = r.data || {};
        // Hide when there's nothing meaningful to show
        if ((d.peak_intensity || 0) < SHOW_THRESHOLD) {
          setData(null);
        } else {
          setData(d);
        }
      } catch {
        if (!cancelledRef.current) setData(null);
      }
    };

    fetchOnce();
    timer = setInterval(fetchOnce, POLL_MS);

    return () => {
      cancelledRef.current = true;
      clearInterval(timer);
    };
  }, [location?.latitude, location?.longitude]);

  return data;
}
